import { useContext, useContextSelector } from 'use-context-selector';
import { AccountDetailsContext } from './AccountDetailsContext';

const useAccountDetailsState = () => {
  const context = useContext(AccountDetailsContext);

  if (context === undefined) {
    throw new Error('useAccountDetailsState must be used within AccountDetailsProvider');
  }

  return context;
};

const useAccountDetailsSelectedAccount = () => {
  const context = useContextSelector(AccountDetailsContext, state => state.selectedAccount);

  if (context === undefined) {
    throw new Error('useAccountDetailsAccounts must be used within AccountDetailsProvider');
  }

  return context;
};

const useAccountDetailsSelectedAccountDetails = () => {
  const context = useContextSelector(AccountDetailsContext, state => state.selectedAccountDetails);

  if (context === undefined) {
    throw new Error('useAccountDetailsSelectedAccountDetails must be used within AccountDetailsProvider');
  }

  return context;
};

const useAccountDetailsTransactionsTotalItems = () => {
  const context = useContextSelector(
    AccountDetailsContext,
    state => state.accountExtraitTransactionListData?.totalItems ?? 0,
  );

  if (context === undefined) {
    throw new Error('useAccountDetailsTransactionsTotalItems must be used within AccountDetailsProvider');
  }

  return context;
};

const useAccountDetailsExtraitTransactions = () => {
  const context = useContextSelector(
    AccountDetailsContext,
    state => state.accountExtraitTransactionListData?.transactions ?? [],
  );

  if (context === undefined) {
    throw new Error('useAccountDetailsExtraitTransactions must be used within AccountDetailsProvider');
  }

  return context;
};

const useAccountDetailsOnAccountSelect = () => {
  const context = useContextSelector(AccountDetailsContext, state => state.onAccountSelect);

  if (context === undefined) {
    throw new Error('useAccountDetailsOnAccountSelect must be used within AccountDetailsProvider');
  }

  return context;
};

const useAccountDetailsLoadingState = (
  loadingProperty: 'isLoadingAccountDetails' | 'isLoadingAccountExtraitTransactionList',
) => {
  const context = useContextSelector(AccountDetailsContext, state => state.loadingState);

  if (context === undefined) {
    throw new Error('useAccountDetailsLoadingState must be used within AccountDetailsProvider');
  }

  return context[loadingProperty];
};

const useAccountDetailsErrorState = (errorProperty: 'accountDetailsError' | 'accountExtraitTransactionListError') => {
  const context = useContextSelector(AccountDetailsContext, state => state.errorState);

  if (context === undefined) {
    throw new Error('useAccountDetailsErrorState must be used within AccountDetailsProvider');
  }

  return context[errorProperty];
};

const useOnTransactionFiltersChange = () => {
  const context = useContextSelector(AccountDetailsContext, state => state.onTransactionListFiltersChange);

  if (context === undefined) {
    throw new Error('useOnTransactionFiltersChange must be used within AccountDetailsProvider');
  }

  return context;
};

const useTransactionListFilters = () => {
  const context = useContextSelector(AccountDetailsContext, state => state.accountExtraitTransactionListFilters);

  if (context === undefined) {
    throw new Error('useTransactionListFilters must be used within AccountDetailsProvider');
  }

  return context;
};

const useOnResetTransactionListFilters = () => {
  const context = useContextSelector(AccountDetailsContext, state => state.onResetTransactionListFilters);

  if (context === undefined) {
    throw new Error('onResetTransactionListFilters must be used within AccountDetailsProvider');
  }

  return context;
};

const useOnRefetchAccountDetails = () => {
  const context = useContextSelector(AccountDetailsContext, state => state.onRefetchAccountDetails);
  if (context === undefined) {
    throw new Error('useOnRefetchAccountDetails must be used within AccountDetailsProvider');
  }

  return context;
};

const useOnRefetchAccountExtraitTransactionList = () => {
  const context = useContextSelector(AccountDetailsContext, state => state.onRefetchAccountExtraitTransactionList);
  if (context === undefined) {
    throw new Error('useOnRefetchAccountDetails must be used within AccountDetailsProvider');
  }

  return context;
};

const useGetLastPaginationPageVisited = () => {
  const context = useContextSelector(AccountDetailsContext, state => state.lastPaginationPageVisited);
  if (context === undefined) {
    throw new Error('useGetLastPaginationPageVisited must be used within AccountDetailsProvider');
  }

  return context;
};

const useOnSetLastPaginationPageVisited = () => {
  const context = useContextSelector(AccountDetailsContext, state => state.onSetLastPaginationPageVisited);
  if (context === undefined) {
    throw new Error('useOnSetLastPaginationPageVisited must be used within AccountDetailsProvider');
  }

  return context;
};

export {
  useAccountDetailsTransactionsTotalItems,
  useOnTransactionFiltersChange,
  useAccountDetailsState,
  useAccountDetailsSelectedAccount,
  useAccountDetailsSelectedAccountDetails,
  useAccountDetailsExtraitTransactions,
  useAccountDetailsOnAccountSelect,
  useAccountDetailsLoadingState,
  useAccountDetailsErrorState,
  useTransactionListFilters,
  useOnResetTransactionListFilters,
  useOnRefetchAccountDetails,
  useOnRefetchAccountExtraitTransactionList,
  useGetLastPaginationPageVisited,
  useOnSetLastPaginationPageVisited,
};
